// The Asset library's list state, shared by the Console page and the Portal page (HD-10). The calls are the pre-HD-10
// page's, in its order on arrival: GET /api/clients, GET /api/assets?page=1, GET /api/asset-folders. The old page made
// the clients call once per panel (up to three identical GETs); it is made once here.
// GET /api/assets pages by 24 (the API's PAGE_SIZE); Load more appends the next page. The search waits 400 ms after typing.
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { listParams } from '../../lib/assetLibrary';

export function useAssetLibrary() {
  const [clients, setClients]     = useState([]);
  const [folders, setFolders]     = useState([]);
  const [folderId, setFolderId]   = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ]                 = useState('');
  const [propType, setPropType]   = useState('');
  const [clientId, setClientId]   = useState('');
  const [assets, setAssets]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [searchType, setSearchType] = useState(null);
  const [state, setState]         = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const pageRef = useRef(1);

  useEffect(() => { api.get('/api/clients').then(r => setClients(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setState('loading');
    pageRef.current = 1;
    try {
      const res = await api.get(`/api/assets?${listParams({ page: 1, q, propType, clientId, folderId })}`);
      setAssets(res.data.assets);
      setTotal(res.data.total);
      setSearchType(res.data.searchType || null);
      setState('data');
    } catch { setState('error'); }
  }, [q, propType, clientId, folderId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { api.get('/api/asset-folders').then(r => setFolders(r.data)).catch(() => {}); }, []);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const nextPage = pageRef.current + 1;
      const res = await api.get(`/api/assets?${listParams({ page: nextPage, q, propType, clientId, folderId })}`);
      setAssets(a => [...a, ...res.data.assets]);
      setTotal(res.data.total);
      pageRef.current = nextPage;
    } catch { /* the button stays; the next press tries again */ } finally { setLoadingMore(false); }
  }, [q, propType, clientId, folderId]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);
  const byName = (a, b) => a.name.localeCompare(b.name);
  const folderCreated = useCallback(f => setFolders(prev => [...prev, f].sort(byName)), []);
  const folderRenamed = useCallback(f => setFolders(prev => prev.map(x => (x.id === f.id ? { ...x, name: f.name } : x)).sort(byName)), []);
  const folderDeleted = useCallback(id => { setFolders(prev => prev.filter(x => x.id !== id)); setFolderId(cur => (cur === id ? null : cur)); }, []);

  const filtered = !!(q || propType || clientId);
  const clearFilters = () => { setSearchInput(''); setQ(''); setPropType(''); setClientId(''); };

  return {
    clients, folders, folderId, setFolderId, searchInput, setSearchInput, q, propType, setPropType, clientId, setClientId,
    assets, total, searchType, state, loadingMore, load, loadMore, refresh, hasMore: assets.length < total,
    folderCreated, folderRenamed, folderDeleted, filtered, clearFilters,
    folderName: folders.find(f => f.id === folderId)?.name || null,
  };
}
